import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import Layout from './components/Layout';
import ScrollToTop from './components/ScrollToTop';
import { usePageTracking } from './hooks/monitoring';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { LoadingSpinner } from './components/ui';
import { ProtectedRoute, AdminRoute } from './components/ProtectedRoute';
import AdminShell from './components/AdminShell';
import './App.css';
import Home from './pages/Home';

// Lazy load pages for better performance
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const Products = lazy(() => import('./pages/Products'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Checkout = lazy(() => import('./pages/Checkout'));
// const Portfolio = lazy(() => import('./pages/Portfolio'));
const Contact = lazy(() => import('./pages/Contact'));
// const Blog = lazy(() => import('./pages/Blog'));
const UIComponents = lazy(() => import('./pages/UIComponents'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Console = lazy(() => import('./pages/Console'));
const Download = lazy(() => import('./pages/Download'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const JoinOrganization = lazy(() => import('./pages/JoinOrganization'));
const Organizations = lazy(() => import('./pages/Organizations'));
const OrgPlans = lazy(() => import('./pages/OrgPlans'));
const Subscriptions = lazy(() => import('./pages/Subscriptions'));
const InviteUser = lazy(() => import('./pages/InviteUser'));
const RoleManagement = lazy(() => import('./pages/RoleManagement'));
const GroupManagement = lazy(() => import('./pages/GroupManagement'));
const ResourceManagement = lazy(() => import('./pages/ResourceManagement'));
const PlanManagement = lazy(() => import('./pages/PlanManagement'));
const Profile = lazy(() => import('./pages/Profile'));
const ShopVerificationPage = lazy(() => import('./pages/ShopVerificationPage'));
const RegisterShopPage = lazy(() => import('./pages/RegisterShopPage'));
const ShopRegistrationSuccess = lazy(
  () => import('./pages/ShopRegistrationSuccess')
);
const ShopDocumentsPage = lazy(() => import('./pages/ShopDocumentsPage'));
const LinkAccountPage = lazy(() => import('./pages/LinkAccountPage'));
const CustomersPage = lazy(() => import('./pages/CustomersPage'));

function App() {
  // Automatically track page views
  usePageTracking();

  return (
    <ThemeProvider>
      <ToastProvider position="top-right">
        <ScrollToTop />
        <Layout>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/products" element={<Products />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/checkout" element={<Checkout />} />
              {/* <Route path="/portfolio" element={<Portfolio />} /> */}
              <Route path="/contact" element={<Contact />} />
              {/* <Route path="/blog" element={<Blog />} /> */}
              {/* <Route path="/newsletter" element={<NewsletterPage />} /> */}
              <Route path="/ui-components" element={<UIComponents />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/refund" element={<RefundPolicy />} />
              <Route path="/download" element={<Download />} />
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <Onboarding />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/join-org"
                element={
                  <ProtectedRoute>
                    <JoinOrganization />
                  </ProtectedRoute>
                }
              />

              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={<Navigate to="/console" replace />}
              />
              <Route
                path="/console"
                element={
                  <ProtectedRoute>
                    <AdminShell>
                      <Console />
                    </AdminShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/organizations"
                element={
                  <ProtectedRoute>
                    <AdminShell>
                      <Organizations />
                    </AdminShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/org-plans"
                element={
                  <ProtectedRoute>
                    <AdminShell>
                      <OrgPlans />
                    </AdminShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscriptions"
                element={
                  <ProtectedRoute>
                    <AdminShell>
                      <Subscriptions />
                    </AdminShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/organizations/:orgId/invite"
                element={
                  <ProtectedRoute>
                    <InviteUser />
                  </ProtectedRoute>
                }
              />

              {/* Admin-only Routes */}
              <Route
                path="/admin"
                element={<Navigate to="/console" replace />}
              />
              <Route
                path="/admin/roles"
                element={
                  <AdminRoute>
                    <AdminShell>
                      <RoleManagement />
                    </AdminShell>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/groups"
                element={
                  <AdminRoute>
                    <AdminShell>
                      <GroupManagement />
                    </AdminShell>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/resources"
                element={
                  <AdminRoute>
                    <AdminShell>
                      <ResourceManagement />
                    </AdminShell>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/plans"
                element={
                  <AdminRoute>
                    <AdminShell>
                      <PlanManagement />
                    </AdminShell>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/shops/verification"
                element={
                  <AdminRoute>
                    <AdminShell>
                      <ShopVerificationPage />
                    </AdminShell>
                  </AdminRoute>
                }
              />
              <Route
                path="/shops/register"
                element={
                  <ProtectedRoute>
                    <RegisterShopPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/shops/:shopId/success"
                element={
                  <ProtectedRoute>
                    <ShopRegistrationSuccess />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/shops/:shopId/documents"
                element={
                  <ProtectedRoute>
                    <ShopDocumentsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/customers"
                element={
                  <ProtectedRoute>
                    <CustomersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/link-account"
                element={
                  <ProtectedRoute>
                    <LinkAccountPage />
                  </ProtectedRoute>
                }
              />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </Layout>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
